import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ApiError } from "../../../lib/redux";
import { OptimizedPlan } from "../../../models";
import { acceptRun, clearAcceptAPI, pollRun } from "../fleetOptimizationSlice";
import { getDeliveries } from "../../Deliveries/deliveriesSlice";
import { getPickups } from "../../Pickups/pickUpsSlice";
import { getDumpYards, getDumpYardTemplates } from "../../DumpYards/dumpYardsSlice";
import { OptionDetail } from "./OptionDetail";
import { InputSummary } from "./InputSummary";
import { AcceptPlanModal } from "./AcceptPlanModal";
import { humanDuration } from "../RunsList/RunsList";

import "./Result.scss";

function FeasibleBadge({ plan }: { plan: OptimizedPlan }) {
  const feasible = plan.routes.filter((r) => r.feasible).length;
  const total = plan.routes.length;
  const allFeasible = feasible === total;
  return (
    <span
      className={`feasible-badge ${allFeasible ? "feasible-badge--ok" : "feasible-badge--warn"}`}
    >
      {feasible}/{total}
    </span>
  );
}

const TERMINAL_STATUSES = new Set(["complete", "failed"]);

export function Result() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentRun = useAppSelector((state) => state.fleetOptimization.currentRun);
  const getAPI = useAppSelector((state) => state.fleetOptimization.getAPI);
  const acceptAPI = useAppSelector((state) => state.fleetOptimization.acceptAPI);

  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(0);
  // Which option's accept-confirmation modal is open (null = none).
  const [acceptIndex, setAcceptIndex] = React.useState<number | null>(null);

  const openAccept = (index: number) => {
    dispatch(clearAcceptAPI());
    setAcceptIndex(index);
  };

  const confirmAccept = async () => {
    if (!id || acceptIndex == null) return;
    const result = await dispatch(acceptRun({ id, planIndex: acceptIndex }));
    if (acceptRun.fulfilled.match(result)) {
      const s = result.payload.schedules;
      const totals = s.reduce(
        (a, r) => ({
          d: a.d + r.deliveries,
          p: a.p + r.pickups,
        }),
        { d: 0, p: 0 }
      );
      toast.success(
        `Schedule updated for ${result.payload.date}: ${s.length} driver(s), ${totals.d} deliveries, ${totals.p} pickups.`
      );
      setAcceptIndex(null);
      dispatch(pollRun(id)); // refresh the run
    }
    // On failure the error stays in acceptAPI and renders in the modal.
  };

  const acceptError = acceptAPI.error as ApiError | undefined;
  const acceptErrorText = acceptError
    ? acceptError.title || acceptError.detail || "Accept failed. Please try again."
    : undefined;

  const status = currentRun?.status;
  const isTerminal = !!status && TERMINAL_STATUSES.has(status);

  // Polling effect — re-runs when id changes OR when terminal boundary is crossed.
  // When isTerminal becomes true, the effect cleanup from the prior run clears the
  // interval, and the new run skips scheduling a new one.
  React.useEffect(() => {
    if (!id) return;

    let mounted = true;

    // Always fire an immediate fetch on mount (or when id changes)
    dispatch(pollRun(id));

    // If we already have a terminal status, don't start an interval
    if (isTerminal) return;

    const intervalId = setInterval(() => {
      if (mounted) {
        dispatch(pollRun(id));
      }
    }, 2000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isTerminal]);

  // Load date-scoped data (deliveries, pickups, dumpYards) so OptionDetail and
  // RouteMap can resolve stop names/coords. Keyed on date only — avoids re-fetching
  // on every poll cycle while still running when the date becomes known.
  const runDate = currentRun?.date;
  React.useEffect(() => {
    if (!runDate) return;
    dispatch(getDeliveries(runDate));
    dispatch(getPickups(runDate));
    dispatch(getDumpYards(runDate));
    // Templates back InputSummary's forced-dump-yard name resolution (template ids).
    dispatch(getDumpYardTemplates());
  }, [runDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Render states ----

  // Show spinner when:
  //   • we have no currentRun data yet (initial load), OR
  //   • the run is actively pending/running
  const noDataYet = !currentRun && (getAPI.loading || (!getAPI.value && !getAPI.error));
  const isActivelyRunning = status === "pending" || status === "running";

  if (noDataYet || isActivelyRunning) {
    return (
      <div className="fleet-optimization-result">
        <div className="result-loading">
          <Oval color="#131313" height={40} width={40} secondaryColor="#FBD20A" />
          <span className="solving-text">Solving…</span>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="fleet-optimization-result">
        <div className="result-error">{currentRun?.error || "Optimization failed."}</div>
      </div>
    );
  }

  if (status === "complete" && currentRun?.result?.options) {
    const options = currentRun.result.options;
    const solve = currentRun.result.solve;
    const fmtDuration = humanDuration;

    return (
      <div className="fleet-optimization-result">
        <div className="result-header">
          <h2 className="result-title">Optimization Results</h2>
          <span className="result-date">{currentRun.date}</span>
          <Link className="scorecard-link btn" to={`/fleet-optimization/${currentRun.id}/scorecard`}>
            Compare to final schedule
          </Link>
          {solve && solve.durationMs > 0 && (
            <span
              className="result-solve"
              title={
                [
                  solve.totalIterations
                    ? `${solve.totalIterations.toLocaleString()} iterations`
                    : "",
                  solve.workers ? `${solve.workers} workers` : "",
                  solve.solveBudgetMs
                    ? `${fmtDuration(solve.solveBudgetMs)} budget`
                    : "",
                ]
                  .filter(Boolean)
                  .join(" · ") || undefined
              }
            >
              Solved in {fmtDuration(solve.durationMs)}
            </span>
          )}
          {currentRun.input && (
            <button
              className="result-copy-inputs"
              onClick={() =>
                navigate(
                  `/fleet-optimization/new?date=${currentRun.input.date}`,
                  { state: { prefill: currentRun.input } }
                )
              }
            >
              Copy inputs to new run
            </button>
          )}
        </div>

        {currentRun.input && <InputSummary input={currentRun.input} />}

        <div className="table-container">
          <table className="new-table options-table">
            <thead>
              <tr>
                <th>Option</th>
                <th>Operational cost</th>
                <th>Penalties (incl. maintenance)</th>
                <th>Total cost</th>
                <th>Miles</th>
                <th>Drive hrs</th>
                <th>Service hrs</th>
                <th>Feasible</th>
                <th>Δ vs best</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {options.map((plan, i) => {
                const miles = (plan.meters / 1609.34).toFixed(1);
                const driveHrs = (plan.drivingSeconds / 3600).toFixed(1);
                const serviceHrs = (plan.serviceSeconds / 3600).toFixed(1);
                // Operational cost = real cash outlay: fuel + wage + dump fees
                // (mirrors the runs-list page). Maintenance is a notional per-mile
                // wear estimate, grouped under penalties instead.
                const operational = plan.fuelCost + plan.wageCost + plan.dumpFees;
                // Not-billed chips: maintenance first, then each soft penalty by type.
                const penaltyChips: { type: string; amount: number }[] = [];
                if (plan.maintenanceCost && plan.maintenanceCost > 0) {
                  penaltyChips.push({ type: "maintenance", amount: plan.maintenanceCost });
                }
                penaltyChips.push(...(plan.penalties ?? []));
                const penaltyTotal = penaltyChips.reduce((s, p) => s + p.amount, 0);
                const delta =
                  i === 0 ? "—" : `+$${(plan.cost - options[0].cost).toFixed(2)}`;
                const isExpanded = expandedIndex === i;
                const allFeasible = plan.routes.every((r) => r.feasible);

                return (
                  <React.Fragment key={i}>
                    <tr
                      className={`option-row clickable-row ${isExpanded ? "option-row--expanded" : ""}`}
                      onClick={() =>
                        setExpandedIndex((prev) => (prev === i ? null : i))
                      }
                      role="button"
                      aria-expanded={isExpanded}
                    >
                      <td className="option-index">{i + 1}</td>
                      <td className="cost-operational">${operational.toFixed(2)}</td>
                      <td className="penalties-cell">
                        {penaltyTotal === 0 ? "—" : `$${penaltyTotal.toFixed(2)}`}
                        {penaltyChips.length > 0 && (
                          <span className="penalties-breakdown">
                            {penaltyChips.map((p, j) => (
                              <span key={j} className="penalty-chip">
                                {p.type.replace(/_/g, " ")} ${p.amount.toFixed(2)}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="cost-total">${plan.cost.toFixed(2)}</td>
                      <td>{miles}</td>
                      <td>{driveHrs}</td>
                      <td>{serviceHrs}</td>
                      <td>
                        <FeasibleBadge plan={plan} />
                      </td>
                      <td className="delta-col">{delta}</td>
                      <td className="accept-col">
                        <button
                          className="accept-plan-button"
                          disabled={!allFeasible}
                          title={
                            allFeasible
                              ? "Apply this plan to the schedule (overrides current)"
                              : "Plan has an infeasible route and cannot be accepted"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            openAccept(i);
                          }}
                        >
                          Accept
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="option-detail-row">
                        <td colSpan={10}>
                          <OptionDetail plan={plan} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <AcceptPlanModal
          isOpen={acceptIndex != null}
          optionNumber={(acceptIndex ?? 0) + 1}
          date={currentRun.date}
          loading={acceptAPI.loading}
          error={acceptErrorText}
          onConfirm={confirmAccept}
          onCancel={() => setAcceptIndex(null)}
        />
      </div>
    );
  }

  // Fallback (e.g. getAPI error with no currentRun)
  return (
    <div className="fleet-optimization-result">
      <div className="result-error">Failed to load run. Please try again.</div>
    </div>
  );
}
